import React, {useEffect, useState} from 'react';
import {claimDailyBonus} from "../../services/bonus";
import {useSnackbar} from "../../context/SnackbarContext";
import {parseLaunchParams} from "../../utils/launch_params";
import {pluralize} from "../../utils/plural";
import BonusStore from '../../store/bonus';

const HINT_META = {
    time_boost: {
        title: "Добавить время",
        description: "+20 секунд к таймеру",
    },
    word_contour: {
        title: "Показать контур",
        description: "Подсветка контура одного ненайденного слова",
    },
};

const BADGE_CLASSES = "inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold tracking-wide bg-yellow-500 text-gray-900 border border-yellow-600";

const DailyBonus = () => {
    const [streak, setStreak] = useState(0);
    const [rewardInfo, setRewardInfo] = useState({
        coins: 0,
        hintRewards: {},
        description: "",
        alreadyClaimed: false,
    });
    const {showSuccessSnackbar} = useSnackbar();
    const params = parseLaunchParams();

    useEffect(() => {
        if (!params.vk_user_id) {
            return;
        }

        claimDailyBonus()
            .then(response => {
                const {
                    streak_days,
                    coins_awarded,
                    hint_rewards,
                    reward_description,
                    last_reward_coins,
                    last_reward_hint_rewards,
                    already_claimed,
                } = response.data.response;

                const grantedHintRewards = hint_rewards || {};
                const hasGrantedHints = Object.values(grantedHintRewards).some(amount => amount > 0);
                const totalGrantedHints = Object.values(grantedHintRewards).reduce((sum, amount) => sum + amount, 0);
                // Формируем список названий подсказок для снекбара.
                const grantedHintTitles = Object.entries(grantedHintRewards)
                    .filter(([, amount]) => amount > 0)
                    .map(([hintType]) => (HINT_META[hintType]?.title || "Подсказка"));

                const displayCoins = already_claimed ? 0 : (last_reward_coins ?? coins_awarded);
                const lastHintRewards = last_reward_hint_rewards || {};
                // Показываем подсказки только в момент их получения (первый заход в сутки).
                const displayHintRewards = already_claimed
                    ? {}
                    : hasGrantedHints
                        ? grantedHintRewards
                        : Object.values(lastHintRewards).some(amount => amount > 0)
                            ? lastHintRewards
                            : {};

                const description = reward_description || (displayCoins > 0 ? `${displayCoins} монет` : "Случайный бонус");

                setStreak(streak_days);
                setRewardInfo({
                    coins: displayCoins,
                    hintRewards: displayHintRewards,
                    description,
                    alreadyClaimed: Boolean(already_claimed),
                });

                const hasReward = coins_awarded > 0 || hasGrantedHints;
                if (hasReward) {
                    const hintWord = pluralize(totalGrantedHints, 'подсказку', 'подсказки', 'подсказок');
                    const rewardMessage = hasGrantedHints
                        ? totalGrantedHints > 1
                            ? `Вы получили ${hintWord}: ${grantedHintTitles.map(title => `«${title}»`).join(', ')}`
                            : `Вы получили ${hintWord} «${grantedHintTitles[0]}»`
                        : reward_description
                            ? `Вы получили «${reward_description}»`
                            : coins_awarded > 0
                                ? `Вы получили ${coins_awarded} ${pluralize(coins_awarded, 'монету', 'монеты', 'монет')}`
                                : "Вы получили случайный бонус";
                    showSuccessSnackbar(rewardMessage);
                }

                if (coins_awarded > 0) {
                    BonusStore.debit(coins_awarded);
                }
            })
            .catch(error => {
                console.error("Error claiming daily bonus", error);
            });
    }, [params.vk_user_id]);

    const streakText = streak > 0 ? `Ваша серия: ${streak} ${pluralize(streak, 'день', 'дня', 'дней')}` : "Зайдите завтра, чтобы начать серию!";
    const icon = streak > 1 ? '🔥' : '🎁';
    const hintEntries = Object.entries(rewardInfo.hintRewards || {}).filter(([, amount]) => amount > 0);

    return (
        <div className="w-full p-4 bg-gray-200 border border-gray-300/50 rounded-xl flex flex-col shadow-sm">
            <div className="flex items-center justify-between">
                <div className="flex items-center">
                    <div className="text-3xl mr-4">{icon}</div>
                    <div>
                        <p className="font-bold text-gray-800">Ежедневный бонус</p>
                        <p className="text-sm text-gray-700">{streakText}</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-7 gap-2 mt-4">
                {Array.from({length: 7}).map((_, index) => {
                    const dayNumber = index + 1;
                    const isCompleted = dayNumber <= streak;
                    const stateClasses = isCompleted ? "bg-yellow-500" : "bg-gray-400/50";

                    return (
                        <div key={dayNumber}
                             className={`w-full h-2 rounded-md transition-all duration-300 ${stateClasses}`}/>
                    );
                })}
            </div>

            <div className="grid grid-cols-1 gap-3 mt-4">
                {rewardInfo.coins > 0 && (
                    <div className="relative bg-gray-100 rounded-xl p-4 shadow-sm border border-gray-300/60">
                        <div className="flex items-start justify-between gap-4 text-gray-800">
                            <div>
                                <p className="text-sm font-semibold">Бонусные монеты</p>
                                <p className="text-xs mt-1">{`${rewardInfo.coins} монет`}</p>
                            </div>
                            <span className={BADGE_CLASSES}>{`+${rewardInfo.coins}`}</span>
                        </div>
                    </div>
                )}
                {hintEntries.length > 0 && (
                    <div className="grid grid-cols-1 gap-3">
                        {hintEntries.map(([hintType, count]) => {
                            const meta = HINT_META[hintType] || {title: "Подсказка", description: "Деталь бонуса"};
                            return (
                                <div key={hintType}
                                     className="relative bg-gray-100 rounded-xl p-4 shadow-sm border border-gray-300/60">
                                    <div className="flex items-start justify-between gap-4 text-gray-800">
                                        <div>
                                            <p className="text-sm font-semibold">Подсказка «{meta.title}»</p>
                                            <p className="text-xs mt-1">{meta.description}</p>
                                        </div>
                                        <span className={BADGE_CLASSES}>{`+${count}`}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DailyBonus;
